import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Heart, AlertCircle, Mail, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { authApi, LoginRequest } from '../../lib/api';

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setAccountError(null); // Clear any previous account errors
    dispatch(loginStart());

    try {
      const response = await authApi.login(formData);
      
      if (response.data.success) {
        // Handle Super Admin backend response structure
        const responseData = response.data as any;
        const { user, accessToken, refreshToken, expiresIn, customer, license } = responseData;
        
        dispatch(loginSuccess({
          user: {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            phone: '', // Super Admin doesn't provide phone, use empty string
            role: user.role.toUpperCase() as 'ADMIN' | 'DOCTOR' | 'NURSE' | 'RECEPTIONIST',
            isActive: user.isActive,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt
          },
          token: accessToken,
          refreshToken: refreshToken
        }));
        
        // Store customer and license info in localStorage for HMS use
        localStorage.setItem('hms_customer', JSON.stringify(customer));
        localStorage.setItem('hms_license', JSON.stringify(license));
        
        toast.success(`Welcome back, ${user.firstName}!`);
        
        // Navigate to dashboard after successful login
        navigate('/dashboard');
      } else {
        dispatch(loginFailure());
        const responseData = response.data as any;
        const errorMessage = responseData.error || responseData.message || 'Login failed';
        
        // Check if this is an account status error that needs special handling
        if (isAccountStatusError(errorMessage)) {
          setAccountError(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      dispatch(loginFailure());
      // Extract the specific error message from the backend response
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Login failed. Please try again.';
      
      // Check if this is an account status error that needs special handling
      if (isAccountStatusError(errorMessage)) {
        setAccountError(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to determine if error requires special handling
  const isAccountStatusError = (errorMessage: string): boolean => {
    const accountStatusErrors = [
      'suspended',
      'cancelled',
      'archived', 
      'deactivated',
      'expired',
      'no active license'
    ];
    return accountStatusErrors.some(keyword => 
      errorMessage.toLowerCase().includes(keyword)
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    // Clear account error when user starts typing again
    if (accountError) {
      setAccountError(null);
    }
  };

  // Render account status error screen
  if (accountError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-red-200">
            {/* Error Header */}
            <div className="text-center">
              <div className="mx-auto h-12 w-12 sm:h-14 sm:w-14 bg-red-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <AlertCircle className="h-6 w-6 sm:h-7 sm:w-7 text-red-600" />
              </div>
              <h2 className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
                Account Access Issue
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Hospital Management System
              </p>
            </div>

            {/* Error Message */}
            <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium text-center">
                {accountError}
              </p>
            </div>

            {/* Support Information */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900 text-center">
                Need Help?
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>Email: support@hms.kamranrasheed.com</span>
                </div>
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>Phone: +1 (555) 123-4567</span>
                </div>
              </div>

              <div className="text-center text-xs text-gray-500 mt-4">
                Please contact our support team for assistance with your account.
                <br />
                Reference your email address: <span className="font-medium">{formData.email}</span>
              </div>
            </div>

            {/* Back to Login */}
            <div className="mt-8">
              <button
                onClick={() => setAccountError(null)}
                className="w-full py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                Try Different Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-12 w-12 sm:h-14 sm:w-14 bg-primary-600 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
              <Heart className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </div>
            <h2 className="mt-4 sm:mt-6 text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
              Welcome to<br className="sm:hidden" />
              <span className="sm:inline block mt-1 sm:mt-0 sm:ml-2">HMS Portal</span>
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Hospital Management System
            </p>
          </div>

          {/* Form */}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="on" noValidate>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 